import { useState } from "react"
import type { ComponentProps } from "react"
import { Alert, Box, CircularProgress, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { ERROR, OK } from "helpers/data-statuses"
import type { SkillTier } from "connected-components/skills/types"
import { useSkillFileContent } from "resources/skills"
import type { SkillFileEntry } from "resources/skills"
import { ChatMarkdown } from "../chatbox/chat-markdown"
import { stripFrontmatter } from "./skill-markdown"

const VIEWER_SX = { display: "flex", flexDirection: "column", gap: 1, minWidth: 0 } satisfies SxProps<Theme>
const SOURCE_SX = {
  m: 0,
  p: 1.5,
  overflowX: "auto",
  fontSize: "0.8125rem",
  lineHeight: 1.5,
  bgcolor: "action.hover",
  borderRadius: 1,
  whiteSpace: "pre",
} satisfies SxProps<Theme>
const LOADING_SX = { display: "flex", justifyContent: "center", p: 2 } satisfies SxProps<Theme>
const NOTICE_SX = { color: "text.secondary", py: 1.5 } satisfies SxProps<Theme>
function ExternalLink({ children, ...props }: ComponentProps<"a">) {
  return <a target="_blank" rel="noopener noreferrer" {...props}>{children}</a>
}

const MARKDOWN_OPTIONS = { disableParsingRawHTML: true, overrides: { a: { component: ExternalLink } } }

type ViewerMode = "rendered" | "source"

export interface SkillFileViewerProps {
  tier: SkillTier
  skillId: string
  file: SkillFileEntry
}

// 413 and 415 are permanent for this entry and 404 means it is gone -- telling any of them to try again
// later sends the user round a loop that can never resolve. Only an unrecognised failure is worth retrying.
function fileErrorMessage(path: string, httpStatus: number | null, tier: SkillTier): string {
  if (httpStatus === 413) return `${path} is too large to preview. Download the bundle to read it.`
  if (httpStatus === 415) return `${path} is not valid UTF-8 text, so it has no preview.`
  if (httpStatus === 404 || (httpStatus === 403 && tier === "shared")) return `${path} is no longer in this skill's bundle.`
  if (httpStatus === 400) return "This skill's bundle could not be read."
  return `${path} did not load. Try again later.`
}

export function SkillFileViewer({ tier, skillId, file }: SkillFileViewerProps) {
  const isMarkdown = file.isText && file.path.toLowerCase().endsWith(".md")
  const [mode, setMode] = useState<ViewerMode>("rendered")
  const { status, text, httpStatus } = useSkillFileContent(tier, skillId, file.isText ? file.path : null)

  if (!file.isText) {
    return <Typography sx={NOTICE_SX} data-testid="kelly-skill-file-notice">This file is not text, so it has no preview.</Typography>
  }
  if (status === ERROR) {
    return <Alert severity="error" data-testid="kelly-skill-file-error">{fileErrorMessage(file.path, httpStatus, tier)}</Alert>
  }
  if (status !== OK || text === null) {
    return <Box sx={LOADING_SX}><CircularProgress size={20} aria-label="Loading file" /></Box>
  }

  return (
    <Box sx={VIEWER_SX} data-testid="kelly-skill-file-viewer">
      {isMarkdown && (
        <ToggleButtonGroup
          exclusive
          size="small"
          value={mode}
          aria-label="Markdown view"
          onChange={(_, value: ViewerMode | null) => { if (value) setMode(value) }}
        >
          <ToggleButton value="rendered">Rendered</ToggleButton>
          <ToggleButton value="source">Source</ToggleButton>
        </ToggleButtonGroup>
      )}
      {isMarkdown && mode === "rendered"
        ? <ChatMarkdown options={MARKDOWN_OPTIONS}>{stripFrontmatter(text)}</ChatMarkdown>
        : <Box component="pre" sx={SOURCE_SX} data-testid="kelly-skill-file-source">{text}</Box>}
    </Box>
  )
}
